/**
 * Boat profile.
 *
 * The profile is not just metadata — it drives the chart. `safetyDepth` decides
 * where the "unsafe water" shading falls and which charted rocks and wrecks get
 * promoted to always-visible isolated-danger symbols. Getting this wrong is the
 * difference between a chart that warns you and one that does not, so the
 * defaults are deliberately conservative.
 *
 * All values SI: metres, metres per second, litres, litres per hour.
 */

import type { LatLon } from './geo';

export interface BoatProfile {
  id: string;
  name: string;

  /** Deepest point of the hull below the waterline, metres. */
  draftM: number;
  /**
   * Under-keel clearance the skipper wants to keep, metres. Added to draft to
   * form the safety depth. Covers swell, squat, tide error and sounding error.
   */
  safetyMarginM: number;
  /** Highest fixed point above the waterline, metres. Optional: open boats have none. */
  airDraftM?: number;

  /** Speed the boat is normally planned at, metres per second. */
  cruiseSpeedMs: number;
  /** Fuel burn at cruise speed, litres per hour. */
  fuelBurnLph: number;
  /** Usable fuel aboard, litres. Optional. */
  fuelCapacityL?: number;

  /** Fraction of tank held in reserve when computing range, 0..1. */
  fuelReserveFraction: number;
}

/**
 * Default margin before the skipper sets their own. One metre of under-keel
 * clearance is a common small-craft rule of thumb and is intentionally more
 * cautious than zero: a profile that has not been configured must not make the
 * chart look safer than it is.
 */
export const DEFAULT_SAFETY_MARGIN_M = 1.0;

export const DEFAULT_FUEL_RESERVE_FRACTION = 0.2;

/** A conservative starting profile for a mid-size recreational power boat. */
export function defaultBoatProfile(id: string): BoatProfile {
  return {
    id,
    name: 'My boat',
    draftM: 1.2,
    safetyMarginM: DEFAULT_SAFETY_MARGIN_M,
    airDraftM: undefined,
    cruiseSpeedMs: 8 * (1852 / 3600), // 8 knots
    fuelBurnLph: 20,
    fuelCapacityL: undefined,
    fuelReserveFraction: DEFAULT_FUEL_RESERVE_FRACTION,
  };
}

/**
 * Depth at or below which water is treated as unsafe for this boat, metres.
 * Water shallower than this is shaded as unsafe and hazards shallower than this
 * are promoted to isolated-danger symbols at every zoom level.
 */
export function safetyDepth(boat: Pick<BoatProfile, 'draftM' | 'safetyMarginM'>): number {
  return boat.draftM + boat.safetyMarginM;
}

/**
 * Depth shading bands, metres, shallow to deep. These are the boundaries of the
 * four-colour depth scheme, derived from the boat rather than fixed, so the same
 * chart shades differently for a dinghy and for a trawler.
 *
 *   [0]  drying / intertidal boundary  (always 0)
 *   [1]  safety depth — the critical line between unsafe and safe water
 *   [2]  deep-water boundary — beyond this, depth is not a concern
 */
export function depthBands(boat: Pick<BoatProfile, 'draftM' | 'safetyMarginM'>): [number, number, number] {
  const safety = safetyDepth(boat);
  // The deep contour is placed well clear of the safety contour so the "plenty
  // of water" colour never abuts the "unsafe" colour without a warning band in
  // between. Twice the safety depth, floored at 10 m, matches how ECDIS deep
  // contours are typically set relative to safety contours.
  return [0, safety, Math.max(10, safety * 2)];
}

/** True if a charted sounding or hazard depth is unsafe for this boat. */
export function isUnsafeDepth(
  depthM: number | null | undefined,
  boat: Pick<BoatProfile, 'draftM' | 'safetyMarginM'>,
): boolean {
  // An unknown depth on a rock or wreck is treated as unsafe. S-57 leaves VALSOU
  // empty when the least depth was never determined, which is a reason for more
  // caution, not less.
  if (depthM === null || depthM === undefined || !Number.isFinite(depthM)) return true;
  return depthM <= safetyDepth(boat);
}

/** True if a charted vertical clearance is too low for this boat. */
export function isUnsafeClearance(
  clearanceM: number | null | undefined,
  boat: Pick<BoatProfile, 'airDraftM'>,
): boolean {
  if (boat.airDraftM === undefined) return false; // no mast, no concern
  if (clearanceM === null || clearanceM === undefined || !Number.isFinite(clearanceM)) return true;
  return clearanceM <= boat.airDraftM;
}

// --- Passage estimates ------------------------------------------------------

export interface PassageEstimate {
  distanceM: number;
  /** Seconds at cruise speed. Infinite if the boat has no speed set. */
  durationS: number;
  /** Litres burnt at cruise. */
  fuelL: number;
  /**
   * Fuel remaining after the passage, litres — undefined when tank size is
   * unknown. Negative means the passage cannot be completed on one tank.
   */
  fuelRemainingL?: number;
  /** True when the passage eats into the configured reserve. */
  exceedsReserve: boolean;
}

/**
 * Time and fuel for a given distance at the boat's cruise speed.
 *
 * This is deliberately a flat-rate model: burn is constant at cruise. Real burn
 * varies with load, sea state, current and hull fouling, so a curve here would
 * imply a precision the app does not have. The number is a planning aid, and the
 * UI must present it as one.
 */
export function estimatePassage(distanceM: number, boat: BoatProfile): PassageEstimate {
  if (distanceM < 0 || !Number.isFinite(distanceM)) {
    throw new RangeError('distanceM must be a non-negative finite number');
  }

  const durationS = boat.cruiseSpeedMs > 0 ? distanceM / boat.cruiseSpeedMs : Infinity;
  const fuelL = Number.isFinite(durationS) ? (durationS / 3600) * boat.fuelBurnLph : Infinity;

  let fuelRemainingL: number | undefined;
  let exceedsReserve = false;
  if (boat.fuelCapacityL !== undefined && Number.isFinite(fuelL)) {
    fuelRemainingL = boat.fuelCapacityL - fuelL;
    exceedsReserve = fuelRemainingL < boat.fuelCapacityL * boat.fuelReserveFraction;
  }

  return { distanceM, durationS, fuelL, fuelRemainingL, exceedsReserve };
}

/** Range on a full tank, metres, keeping the configured reserve intact. */
export function usableRange(boat: BoatProfile): number | undefined {
  if (boat.fuelCapacityL === undefined || boat.fuelBurnLph <= 0) return undefined;
  const usableL = boat.fuelCapacityL * (1 - boat.fuelReserveFraction);
  return (usableL / boat.fuelBurnLph) * 3600 * boat.cruiseSpeedMs;
}

// --- Validation -------------------------------------------------------------

export interface ProfileProblem {
  field: keyof BoatProfile;
  message: string;
}

/**
 * Validate a profile for storage. Returns every problem rather than throwing on
 * the first, so a settings form can show all of them at once.
 */
export function validateBoatProfile(boat: BoatProfile): ProfileProblem[] {
  const problems: ProfileProblem[] = [];
  const positive = (v: number): boolean => Number.isFinite(v) && v > 0;
  const nonNegative = (v: number): boolean => Number.isFinite(v) && v >= 0;

  if (!boat.name.trim()) problems.push({ field: 'name', message: 'Name is required' });
  if (!positive(boat.draftM)) problems.push({ field: 'draftM', message: 'Draft must be greater than zero' });
  if (!nonNegative(boat.safetyMarginM)) problems.push({ field: 'safetyMarginM', message: 'Safety margin cannot be negative' });
  if (boat.airDraftM !== undefined && !positive(boat.airDraftM)) {
    problems.push({ field: 'airDraftM', message: 'Air draft must be greater than zero' });
  }
  if (!positive(boat.cruiseSpeedMs)) problems.push({ field: 'cruiseSpeedMs', message: 'Cruise speed must be greater than zero' });
  if (!nonNegative(boat.fuelBurnLph)) problems.push({ field: 'fuelBurnLph', message: 'Fuel burn cannot be negative' });
  if (boat.fuelCapacityL !== undefined && !positive(boat.fuelCapacityL)) {
    problems.push({ field: 'fuelCapacityL', message: 'Fuel capacity must be greater than zero' });
  }
  if (!(boat.fuelReserveFraction >= 0 && boat.fuelReserveFraction < 1)) {
    problems.push({ field: 'fuelReserveFraction', message: 'Reserve must be between 0% and 100%' });
  }
  return problems;
}

/** Position plus the fix quality the app needs to decide what to trust. */
export interface Fix extends LatLon {
  /** Horizontal accuracy, metres (GeolocationCoordinates.accuracy). */
  accuracyM: number;
  /** Course over ground, degrees true — null when stationary or unknown. */
  courseDeg: number | null;
  /** Speed over ground, metres per second — null when unknown. */
  speedMs: number | null;
  /** Epoch milliseconds of the fix. */
  timestamp: number;
}
